import type { TwnTagProps } from '@/components/atoms/prime/tag/twn-tag/TwnTag.types'
import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'

import { InventoryAdjustmentReasons } from '@/domains/adjustment-groups/types/consts/AdjustmentReason.consts'
import { PLACEHOLDERS } from '@/types/const/placeholders'
import { resolveLabel } from '@/utils/lookup-util/lookupUtil'

/** The three ways a person changes a shelf's stock, in the order every picker lists them. */
const ADJUSTMENT_MODES = ['add', 'remove', 'set'] as const satisfies readonly AdjustmentMode[]

const ADJUSTMENT_GROUP_MODE_LABELS: Record<AdjustmentMode, string> = {
  add: 'Add Stock',
  remove: 'Remove Stock',
  set: 'Set Quantity'
}

const ADJUSTMENT_GROUP_MODE_TAG: Record<AdjustmentMode, { severity: TwnTagProps['severity']; icon: string }> = {
  add: { severity: 'success', icon: 'add' },
  remove: { severity: 'danger', icon: 'remove' },
  set: { severity: 'info', icon: 'edit' }
} as const

const MODE_OPTIONS = ADJUSTMENT_MODES.map(value => ({ label: ADJUSTMENT_GROUP_MODE_LABELS[value], value }))

/**
 * The reasons each mode may carry, in the order each list is shown.
 *
 * Shared by the Quick Update page, the product page's stock slideover and the command palette's stock
 * mode. These lists are the curation. The backend checks less: `StoreInventoryAdjustmentGroupRequest`
 * refuses a system-reserved reason in any mode, and accepts `consignment.correction` in Set mode only.
 * It accepts `consumption` in Remove mode only. The drawer's request accepts only reasons a person may pick.
 */
const REASONS_BY_MODE: Record<AdjustmentMode, readonly InventoryAdjustmentReasons[]> = {
  add: [
    InventoryAdjustmentReasons.CONSIGNMENT_RECEIVED,
    InventoryAdjustmentReasons.RETURN,
    InventoryAdjustmentReasons.RECEIVED,
    InventoryAdjustmentReasons.CORRECTION,
    InventoryAdjustmentReasons.COUNT,
    InventoryAdjustmentReasons.DONATION
  ],
  remove: [
    InventoryAdjustmentReasons.CONSUMPTION,
    InventoryAdjustmentReasons.CONSIGNMENT_RETURNED,
    InventoryAdjustmentReasons.THEFT_LOSS,
    InventoryAdjustmentReasons.DAMAGED,
    InventoryAdjustmentReasons.EXPIRED,
    InventoryAdjustmentReasons.CORRECTION,
    InventoryAdjustmentReasons.COUNT,
    InventoryAdjustmentReasons.DONATION
  ],
  set: [
    InventoryAdjustmentReasons.CONSIGNMENT_RECEIVED,
    InventoryAdjustmentReasons.CONSIGNMENT_RETURNED,
    InventoryAdjustmentReasons.CONSIGNMENT_CORRECTION,
    InventoryAdjustmentReasons.CORRECTION,
    InventoryAdjustmentReasons.COUNT,
    InventoryAdjustmentReasons.DONATION
  ]
}

/**
 * The reason each mode starts with, on every surface: the Quick Update page, the stock slideover and the
 * palette's stock mode.
 *
 * ⚠ **Remove has none, on purpose.** A removal feeds the shrinkage figures, and a default the
 * merchant did not look at reports the wrong cause. So the merchant picks one. Add and Set keep the
 * reason almost every one of them means.
 */
const REASON_DEFAULTS_BY_MODE: Record<AdjustmentMode, InventoryAdjustmentReasons | null> = {
  add: InventoryAdjustmentReasons.RECEIVED,
  remove: null,
  set: InventoryAdjustmentReasons.COUNT
}

const formatAdjustmentGroupMode = (mode: AdjustmentMode | null): string =>
  resolveLabel(ADJUSTMENT_GROUP_MODE_LABELS, mode, PLACEHOLDERS.DASH)

export {
  ADJUSTMENT_GROUP_MODE_LABELS,
  ADJUSTMENT_GROUP_MODE_TAG,
  ADJUSTMENT_MODES,
  MODE_OPTIONS,
  REASON_DEFAULTS_BY_MODE,
  REASONS_BY_MODE,
  formatAdjustmentGroupMode
}
